import React, { useId, useState } from 'react';
import styled from 'styled-components';
import Select from 'v3/components/Select';
import Input from 'v3/components/Input';
import { blocks, reasons } from './form';
import { controlTowerService } from './service';

const Form = styled.div`
  display: grid;
  gap: 20px;
  font-size: 14px;
  .tc-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 16px 24px;
    align-items: start;
  }
  .tc-row-contact {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
  fieldset {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
  }
  legend,
  label {
    font-size: 13px;
    font-weight: 400;
    color: #606775;
    margin-bottom: 6px;
  }
  .tc-error {
    color: #ba2525;
    font-size: 12px;
    margin: 6px 0 0;
  }
  .tc-segmented {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 3px;
    border-radius: 8px;
    background: #eef1f4;
  }
  .tc-segmented-dark {
    background: #fff;
    border: 1px solid #dfe3e8;
  }
  .tc-segment {
    margin: 0;
    color: #3f4652;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  .tc-segment input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
  .tc-segment span {
    display: block;
    padding: 6px 16px;
    border: 1px solid transparent;
    border-radius: 6px;
  }
  .tc-segment input:checked + span {
    background: #fff;
    border-color: #c9d0d8;
    color: #1f2933;
    box-shadow: 0 1px 2px rgba(31, 41, 51, 0.08);
  }
  .tc-segmented-dark .tc-segment input:checked + span {
    background: #1f2933;
    border-color: #1f2933;
    color: #fff;
  }
  .tc-segment input:focus-visible + span {
    outline: 2px solid #2475b4;
    outline-offset: 1px;
  }
  fieldset:disabled .tc-segment {
    cursor: default;
    opacity: 0.7;
  }
  .tc-unavailable {
    display: grid;
    gap: 16px;
    padding: 16px;
    border: 1px solid #f3dcbf;
    border-radius: 8px;
    background: #fdf6ec;
  }
  .tc-unavailable h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #8a4512;
  }
  .tc-hint {
    margin: 0;
    font-size: 12px;
    color: #606775;
  }
  @media (max-width: 700px) {
    .tc-row,
    .tc-row-contact {
      grid-template-columns: 1fr;
    }
  }
`;
function Options({ kind, label, value, onChange, disabled, error, id }) {
  const [failed, setFailed] = useState(false);
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <Select.Async
        id={id}
        value={value}
        onChange={onChange}
        isDisabled={disabled}
        getOptionValue={(option) => String(option.id)}
        getOptionLabel={(option) => `${option.name}${option.cnpj ? ` — ${option.cnpj}` : ''}`}
        onSearch={async (search) => {
          try {
            const options = await controlTowerService.options(kind, search);
            setFailed(false);
            return options;
          } catch {
            setFailed(true);
            return [];
          }
        }}
        error={error}
        NoOptionsComponent={() => (
          <span>
            {failed
              ? 'Falha na consulta. Digite para tentar novamente.'
              : 'Nenhuma opção encontrada'}
          </span>
        )}
        useMenuPortal
      />
      {failed && (
        <p role="alert" className="tc-error">
          Não foi possível buscar {label.toLowerCase()}. Digite para tentar novamente.
        </p>
      )}
    </div>
  );
}
/** Campos da edição; a visualização da configuração salva fica em Summary. */
export default function Fields({ form, change, errors = {}, disabled }) {
  const prefix = useId();
  const segmented = (field, label, choices, dark = false) => (
    <fieldset
      disabled={disabled}
      aria-describedby={errors[field] ? `${prefix}-${field}-error` : undefined}
    >
      <legend>{label}</legend>
      <div className={`tc-segmented${dark ? ' tc-segmented-dark' : ''}`}>
        {choices.map(([value, text]) => (
          <label className="tc-segment" key={String(value)}>
            <input
              type="radio"
              name={`${prefix}-${field}`}
              checked={form[field] === value}
              onChange={() => change(field, value)}
            />
            <span>{text}</span>
          </label>
        ))}
      </div>
      {errors[field] && (
        <p role="alert" className="tc-error" id={`${prefix}-${field}-error`}>
          {errors[field]}
        </p>
      )}
    </fieldset>
  );
  const option = (field, label, errorField = field) => (
    <Options
      id={`${prefix}-${field}`}
      kind={field}
      label={label}
      value={form[field]}
      disabled={disabled}
      onChange={(value) => change(field, value)}
      error={errors[errorField]}
    />
  );
  const yesNo = [
    [true, 'Sim'],
    [false, 'Não'],
  ];
  return (
    <Form>
      <div className="tc-row">
        {segmented('available', 'Disponível', yesNo)}
        {segmented('corridor', 'Veículo de corredor', yesNo)}
      </div>
      {!form.available && (
        <section className="tc-unavailable" aria-labelledby={`${prefix}-unavailable`}>
          <h3 id={`${prefix}-unavailable`}>Indisponibilidade</h3>
          {segmented('unavailabilityReason', 'Motivo', reasons, true)}
          {form.unavailabilityReason === 'DEDICATED' &&
            option('dedicatedClient', 'Cliente dedicado (CNPJ)', 'dedicatedClientId')}
          {['MAINTENANCE', 'TIME_OFF'].includes(form.unavailabilityReason) && (
            <>
              <div className="tc-row">
                {[
                  ['unavailableFrom', 'Início'],
                  ['unavailableUntil', 'Fim'],
                ].map(([field, label]) => (
                  <div key={field}>
                    <label htmlFor={`${prefix}-${field}`}>{label}</label>
                    <Input
                      id={`${prefix}-${field}`}
                      type="datetime-local"
                      // Só data, hora e minuto; o valor salvo sem edição mantém a precisão original.
                      step="60"
                      value={form[field].slice(0, 16)}
                      disabled={disabled}
                      onChange={(event) => change(field, event.target.value)}
                      error={errors[field]}
                    />
                  </div>
                ))}
              </div>
              <p className="tc-hint">Horário de São Paulo.</p>
            </>
          )}
          {form.unavailabilityReason === 'BLOCKED' &&
            segmented('blockReason', 'Motivo do bloqueio', blocks, true)}
        </section>
      )}
      <div className="tc-row tc-row-contact">
        {option('responsible', 'Responsável (filial)', 'responsibleUserId')}
        <div>
          <label htmlFor={`${prefix}-phone`}>Contato</label>
          <Input
            id={`${prefix}-phone`}
            type="tel"
            value={form.contactPhone}
            disabled={disabled}
            onChange={(event) => change('contactPhone', event.target.value)}
            maxLength={30}
            error={errors.contactPhone}
          />
        </div>
      </div>
      {option('costCenter', 'Centro de custo', 'costCenterId')}
    </Form>
  );
}
